import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Show, SignInButton, UserButton, useUser } from "@clerk/react";
import { ShoppingBag, Search, Menu, X } from "lucide-react";
import { useCart } from "../lib/cart";
import { readAck } from "../lib/gate";
import ResearcherGate from "./ResearcherGate";
import { api } from "../lib/api";

const NAV = [
  { to: "/shop", label: "Shop" },
  { to: "/skincare", label: "Skincare" },
  { to: "/coas", label: "COAs" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const CART_PATHS = ["/cart", "/checkout", "/order-received"];

// Skincare pages (and a skincare-only cart) are cosmetic: no researcher gate, no RUO footer.
const isCosmeticContext = (path, hasResearch) =>
  path.startsWith("/skincare") || (CART_PATHS.includes(path) && !hasResearch);

export default function Layout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { count, hasResearch } = useCart();
  const cosmetic = isCosmeticContext(pathname, hasResearch);
  const { user } = useUser();
  const [gateVisible, setGateVisible] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/announcements").then((r) => {
      if (r.data?.length) setAnnouncement(r.data[0].message);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const exempt = cosmetic || pathname.startsWith("/policies") || ["/faq", "/contact"].includes(pathname);
    setGateVisible(!exempt && !readAck());
  }, [pathname, cosmetic]);

  const onSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    navigate(`/shop?q=${encodeURIComponent(query.trim())}`);
    setQuery("");
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F4EBDD] text-[#5A433B] font-sans">
      {announcement && !cosmetic && (
        <div className="bg-[#A8B59A] text-[#2B3024] font-medium text-center text-xs tracking-wide py-2 px-4" data-testid="announcement-banner">
          {announcement}
        </div>
      )}

      <header inert={gateVisible} className="sticky top-0 z-50 backdrop-blur-xl bg-[#F4EBDD]/85 border-b border-[#D8CDBC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="brand-logo" aria-label="Kind of Crunchy Moms home">
            <img src="/images/kocm-logo.png" alt="Kind of Crunchy Moms" className="h-10 sm:h-12 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.slice(1)}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors hover:text-[#59394F] pb-1 border-b ${isActive ? "text-[#59394F] border-[#59394F]" : "text-[#5A433B] border-transparent"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <form onSubmit={onSearch} className="hidden md:flex items-center gap-2 bg-white/70 rounded-full px-4 py-2 border border-[#D8CDBC] focus-within:border-[#A8B59A]">
              <Search size={14} className="text-[#5E6B51]" />
              <input
                data-testid="header-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search catalog"
                className="bg-transparent outline-none text-sm w-32 placeholder:text-[#5E4D5A]/70"
              />
            </form>

            <Link to="/cart" className="relative p-2" data-testid="header-cart">
              <ShoppingBag size={20} className="text-[#5A433B]" />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#C96F53] text-white text-[10px] font-semibold rounded-full w-5 h-5 flex items-center justify-center font-medium">{count}</span>
              )}
            </Link>

            <Show when="signed-out">
              <SignInButton>
                <button data-testid="header-signin" className="hidden sm:inline-flex items-center text-sm px-4 py-2 rounded-full bg-[#59394F] text-[#F4EBDD] hover:bg-[#432A3B] transition-colors">
                  Sign in
                </button>
              </SignInButton>
            </Show>
            <Show when="signed-in">
              <Link to="/account" className="hidden sm:block text-sm text-[#5E4D5A] hover:text-[#59394F] transition-colors" data-testid="header-account">Account</Link>
              <UserButton />
            </Show>

            <button className="lg:hidden p-2 text-[#5A433B]" onClick={() => setMobileOpen((v) => !v)} data-testid="header-mobile-toggle">
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-[#D8CDBC] bg-[#F4EBDD]">
            <div className="px-4 py-4 flex flex-col gap-3">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)} data-testid={`mobile-nav-${n.to.slice(1)}`} className="text-sm py-2 text-[#5A433B] hover:text-[#59394F]">{n.label}</Link>
              ))}
              <div className="border-t border-[#D8CDBC] pt-3 sm:hidden">
                <Show when="signed-out">
                  <Link to="/sign-in" onClick={() => setMobileOpen(false)} data-testid="mobile-signin" className="inline-flex text-sm px-5 py-2 rounded-full bg-[#59394F] text-[#F4EBDD]">Sign in</Link>
                </Show>
                <Show when="signed-in">
                  <Link to="/account" onClick={() => setMobileOpen(false)} data-testid="mobile-account" className="text-sm py-2 text-[#5A433B] hover:text-[#59394F]">My account</Link>
                </Show>
              </div>
            </div>
          </div>
        )}
      </header>

      <main inert={gateVisible} className="flex-1">
        <Outlet />
      </main>

      <footer inert={gateVisible} className="relative overflow-hidden mt-20 bg-[#5A433B] text-[#F4EBDD]/90" data-testid="site-footer">
        <div className="relative max-w-7xl mx-auto px-6 py-16 grid gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="inline-block bg-[#F4EBDD] rounded-2xl px-5 py-3 mb-4" data-testid="footer-logo">
              <img src="/images/kocm-logo.png" alt="Kind of Crunchy Moms" className="h-12 w-auto" />
            </div>
            {!cosmetic && (
              <p className="text-sm max-w-sm leading-relaxed">
                Female-owned research-supply, serving qualified researchers with care.
              </p>
            )}
            {!cosmetic && (
              <div data-testid="footer-ruo">
                <p className="mt-6 text-xs uppercase tracking-[0.3em] text-[#E4BFB6]">For research use only</p>
                <p className="text-sm mt-1 text-[#F4EBDD]/80">Not for human or animal consumption.</p>
              </div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#C8D1BC] mb-4">Shop</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/shop" className="hover:text-[#E4BFB6] transition-colors">Catalog</Link></li>
              <li><Link to="/skincare" className="hover:text-[#E4BFB6] transition-colors">Skincare</Link></li>
              <li><Link to="/coas" className="hover:text-[#E4BFB6] transition-colors">COAs</Link></li>
              <li><Link to="/cart" className="hover:text-[#E4BFB6] transition-colors">Cart</Link></li>
              <li><Link to="/faq" className="hover:text-[#E4BFB6] transition-colors">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-[#E4BFB6] transition-colors">Contact</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#C8D1BC] mb-4">Policies</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/policies/ruo" className="hover:text-[#E4BFB6] transition-colors">Research Use Only</Link></li>
              <li><Link to="/policies/terms" className="hover:text-[#E4BFB6] transition-colors">Terms</Link></li>
              <li><Link to="/policies/privacy" className="hover:text-[#E4BFB6] transition-colors">Privacy</Link></li>
              <li><Link to="/policies/refund" className="hover:text-[#E4BFB6] transition-colors">Refund</Link></li>
              <li><Link to="/policies/shipping" className="hover:text-[#E4BFB6] transition-colors">Shipping</Link></li>
            </ul>
          </div>
        </div>
        <div className="relative border-t border-[#F4EBDD]/15 py-5 text-center text-xs text-[#F4EBDD]/70">
          © {new Date().getFullYear()} Kind of Crunchy Mom LLC. All rights reserved.
        </div>
      </footer>

      {gateVisible && <ResearcherGate onAccept={() => setGateVisible(false)} />}
    </div>
  );
}
